import angular from 'angular';
import amplitude from 'amplitude-js';
import '../controller/controller';
import './storage';
import '../../../vendor/chameleon';

angular.module("core.application.main").factory("core.application.main.factory.analytics", [
    "$rootScope", "$window", "$location",
    "core.application.main.factory.storage",
    function($rootScope, $window, $location, Storage) {

        var expose = {};

        // attach amplitude to browser window for chmln -> amplitude integration
        $window.amplitude = amplitude;

        expose.reset = function() {
            // reset the cached data
            expose.enduringEventProperties(null);
        };

        expose.enduringEventProperties = function(enduringEventProperties) {
            // expects an object of event properties to endure.
            // For example:
            // {
            //     merchantId: "some-merchant-id",
            //     transactionToken: "some-transaction-id",
            // }
            // These will be appended as event properties to events sent to Amplitude.
            // They will continue to be appended until the Analytics factory is reset.
            // Or until the window is closed (they are stored in session storage).
            // Don't store once off properties in here!
            // It's intended to be an easy way to make the same data get sent
            // along with all the events.
            // For example: PAYL-8913 Include merchantId and transactionToken
            // with all events in the checkout process.
            //
            // For data that would be available across both the consumer portal
            // and the checkout process, it would be easier to just set the
            // data on the $rootScope, just like it was done for "category" and
            // "pageName".
            // But for data that's only available in either the checkout process
            // or the consumer portal, it will need to be set here instead.
            // For example, the merchantId and transactionToken aren't available
            // in the consumer portal and shouldn't be sent with consumer portal
            // events anyway.
            return Storage.setSessionStorageItem("enduringEventProperties", enduringEventProperties);
        };

        expose.sendAmplitudeEvent = function(eventName, eventProperties) {
            // eventNames should follow "Verb Noun" convention.
            // For example: "Pressed Continue Button".
            var enduringEventProperties = expose.enduringEventProperties();
            if (eventName && typeof amplitude !== "undefined") {
                if (! eventProperties) {
                    eventProperties = {};
                }
                eventProperties.category = $rootScope.platformName;
                eventProperties.pageName = $rootScope.pageEventName;
                if (enduringEventProperties) {
                    // Add the enduring event properties to the main event properties.
                    Object.keys(enduringEventProperties).forEach(function(key) {
                        eventProperties[key] = enduringEventProperties[key];
                    });
                }
                amplitude.getInstance().logEvent(`Merchant - ${eventName}`, eventProperties);
            }
        };

        expose.prefixPagePath = function(parameters) {
            // expects input:
            // "/orders"
            // The base href will be automatically prefixed like this:
            //     "/au/orders"
            var baseElement, baseUrl;
            if (parameters && parameters.pagePath) {
                baseElement = document.querySelector("base");
                baseUrl = (baseElement && baseElement.getAttribute("href")) || "";
                baseUrl = baseUrl.replace(/\/$/, ""); // remove trailing slash
                // PAYL-6835 We need the base href defined in the html to be
                // prefixed to the url that's logged in google analytics.
                parameters.pagePath = baseUrl + parameters.pagePath;
                parameters.pagePath = expose.removeQueryParametersFromPagePath(parameters.pagePath, parameters.queryParametersToRemove);
                return parameters.pagePath;
            }
        };

        expose.removeQueryParametersFromPagePath = function(pagePath, queryParameters) {
            // remove the given query parameters from the given page path
            // For example if queryParameters = ["test"]:
            //     http://example.com?test=fooble&test2=fooble2
            //     it becomes:
            //     http://example.com?test2=fooble2
            // inspired by https://gist.github.com/smhmic/fca71a9914a755cde241498c8a11d254
            if (pagePath && queryParameters && queryParameters.length) {
                angular.forEach(queryParameters, function(queryParameter) {
                    pagePath = pagePath.replace(new RegExp("([?&])" + queryParameter + "(=[^&]*)?&?", "gi"), "$1");
                });
                // Trim trailing "&"s, and remove "?" if all the parameters are removed.
                pagePath = pagePath.replace(/&+$/, "").replace(/\?$/, "");
            }
            return pagePath;
        };

        expose.sendPageView = function(parameters) {
            // expects:
            // {
            //     pagePath: "/orders"
            // }
            // The base href will be automatically prefixed like this:
            //     "/au/orders"
            if (parameters && parameters.pagePath) {
                parameters.pagePath = expose.prefixPagePath({
                    pagePath: parameters.pagePath,
                    queryParametersToRemove: parameters.queryParametersToRemove
                });
                // PAYL-7092 Even after removing the given query parameters
                // from the page path, they can still be present in the page
                // location, so we need to manually set the location to ensure
                // those query parameters aren't sent to google analytics.
                ga("set", "location", $location.protocol() + "://" + $location.host() + parameters.pagePath);
                // PAYL-6527 ensure the page is set correctly for subsequent events on this page
                ga("set", "page", parameters.pagePath);
                // let google analytics know that a page change has occurred
                ga("send", "pageview");
            }
        };

        expose.sendEvent = function(eventName) {
            var eventProperties = {};
            if (eventName && typeof ga !== "undefined") {
                eventProperties.category = $rootScope.platformName;
                eventProperties.pageName = $rootScope.pageEventName;
                ga("send", "event",
                    eventProperties.category,
                    eventProperties.pageName,
                    eventName,
                    { // these events shouldn't affect the bounce rate
                        nonInteraction: true
                    }
                );
            }
        };

        expose.setUserProperties = function(userProperties) {
            if (typeof amplitude !== "undefined") {
                amplitude.getInstance().setUserProperties(userProperties);
            }
        };

        expose.identifyAmplitude = function(userId, userProperties) {
            if (typeof amplitude !== "undefined" && userId) {
                amplitude.getInstance().setUserId(userId);
                expose.setUserProperties(userProperties);
            }
        };

        expose.deIdentifyAmplitude = function() {
            if (typeof amplitude !== "undefined") {
                amplitude.getInstance().setUserId(null);
            }
        };

        expose.installGtm = function () {
            var iframe = document.createElement("iframe");
            iframe.id = "gtmIframe";
            iframe.setAttribute("src", "/checkout/tracking.html");
            iframe.sandbox = "allow-scripts";
            iframe.classList.add("iframe-hidden");
            document.body.appendChild(iframe);

            return iframe;
        };

        expose.sendGtmEvent = function (event) {
            var iframe = document.getElementById("gtmIframe");
            if (iframe && iframe.contentWindow) {
                if (! event) {
                    event = {};
                }
                event.category = $rootScope.platformName;
                var gtmWindow = iframe.contentWindow;
                gtmWindow.postMessage(event, "*");
            }
            return iframe;
        };

        expose.identifyChameleonUser = function (merchantUserId, userProperties) {
            if (typeof chmln !== "undefined") {
                chmln.identify(merchantUserId, userProperties);
            }
        }

        expose.deIdentifyChameleonUser = function () {
            if (typeof chmln !== "undefined") {
                chmln.clear();
            }
        }

        return expose;

    }
]);
